import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";
import { X, Edit3, User, Mail, Save, Phone } from "lucide-react";
import { useThemeMode } from "../../../theme/ThemeModeContext";

const InputField = ({
  label,
  icon: Icon,
  name,
  value,
  onChange,
  type = "text",
  placeholder,
  required = false,
  isLight,
}) => (
  <div className="space-y-2">
    <label
      className={`text-[11px] font-bold uppercase tracking-[0.2em] flex items-center gap-2 ${isLight ? "text-gray-500" : "text-gray-300/70"}`}
    >
      <Icon size={12} className="text-primary/60" />
      {label} {required && <span className="text-primary">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value || ""}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      className={`w-full rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary/50 transition-all placeholder:text-gray-500 border ${isLight ? "bg-white border-gray-200 text-[#1A1A1A] shadow-sm shadow-gray-100/50" : "bg-white/[0.03] border-white/10 text-white shadow-inner shadow-black/20"}`}
    />
  </div>
);

const EditBlacklistModal = ({ isOpen, onClose, onEdit, initialData }) => {
  const { themeMode } = useThemeMode();
  const isLight = themeMode === "light";
  const [formData, setFormData] = useState({
    VB_id: "",
    VB_Name: "",
    VB_Email: "",
    VB_Phone: "",
    VB_Alert_Type: "Level 01",
  });

  useEffect(() => {
    if (initialData && isOpen) {
      setFormData({
        VB_id: initialData.VB_id || "",
        VB_Name: initialData.VB_Name || "",
        VB_Email: initialData.VB_Email || "",
        VB_Phone:
          initialData.VB_Phone ||
          initialData.VB_Contact_Number ||
          initialData.VB_Mobile ||
          "",
        VB_Alert_Type: initialData.VB_Alert_Type || "Level 01",
      });
    }
  }, [initialData, isOpen]);

  useEffect(() => {
    if (!isOpen || !initialData?.VB_Visitor_id) return;

    const loadPhone = async () => {
      try {
        const { default: VisitorService } =
          await import("../../../services/VisitorService");
        const response = await VisitorService.GetVisitorById(
          initialData.VB_Visitor_id,
        );
        const data = response.data?.ResultSet || response.data;
        const visitor = Array.isArray(data) ? data[0] : data;
        const phone = visitor?.VV_Phone;
        if (phone) {
          setFormData((prev) =>
            prev.VB_Phone ? prev : { ...prev, VB_Phone: phone },
          );
        }
      } catch (error) {
        console.error("Failed to fetch visitor phone:", error);
      }
    };

    loadPhone();
  }, [initialData, isOpen]);

  const handleChange = (e) => {
    let { name, value } = e.target;

    // Real-time filtering
    if (name === "VB_Name") {
      value = value.replace(/[^A-Za-z\s]/g, "");
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onEdit(formData);
    onClose();
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className={`fixed inset-0 backdrop-blur-sm z-[110] ${isLight ? "bg-transparent" : "bg-black/40"}`}
          />

          <div className="fixed inset-0 flex items-center justify-center p-4 z-[111] pointer-events-none">
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className={`w-full max-w-2xl rounded-[26px] pointer-events-auto overflow-hidden flex flex-col max-h-[82vh] border ${isLight ? "bg-white border-gray-200 shadow-[0_20px_48px_rgba(0,0,0,0.14)]" : "bg-[#141416] border-white/10 shadow-[0_20px_64px_rgba(0,0,0,0.52)]"}`}
            >
              {/* Header */}
              <div
                className={`p-4 border-b flex justify-between items-center ${isLight ? "border-gray-200 bg-[#F8F9FA]" : "border-white/5 bg-white/[0.01]"}`}
              >
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                    <Edit3 size={18} />
                  </div>
                  <div>
                    <h2
                      className={`text-[14px] font-semibold tracking-[0.18em] uppercase ${isLight ? "text-[#1A1A1A]" : "text-white"}`}
                    >
                      Edit Blacklisted Visitor
                    </h2>
                    <p
                      className={`text-[10px] tracking-wider ${isLight ? "text-gray-500" : "text-gray-400"}`}
                    >
                      Update the details of this blacklisted visitor
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className={`p-2 rounded-xl transition-colors ${isLight ? "text-gray-500 hover:bg-gray-100 hover:text-[#1A1A1A]" : "text-gray-400 hover:bg-white/5 hover:text-white"}`}
                >
                  <X size={18} />
                </button>
              </div>

              {/* Form Content */}
              <form
                onSubmit={handleSubmit}
                className={`overflow-y-auto p-5 space-y-5 ${isLight ? "bg-[#F8F9FA] scrollbar-thin scrollbar-thumb-gray-300" : "bg-[var(--color-bg-default)] scrollbar-thin scrollbar-thumb-white/10"}`}
              >
                {/* Section 1: Subject Identity */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="col-span-full flex items-center gap-2.5">
                    <div className="w-1 h-3.5 bg-primary rounded-full shadow-[0_0_6px_var(--color-primary)]"></div>
                    <h3 className="text-primary text-[10px] font-bold uppercase tracking-[0.28em]">
                      Visitor Details
                    </h3>
                  </div>

                  <InputField
                    isLight={isLight}
                    label="Full Name"
                    icon={User}
                    name="VB_Name"
                    value={formData.VB_Name}
                    onChange={handleChange}
                    placeholder="Visitor's full name"
                    required
                  />
                  <InputField
                    isLight={isLight}
                    label="Email Address"
                    icon={Mail}
                    name="VB_Email"
                    value={formData.VB_Email}
                    onChange={handleChange}
                    type="email"
                    placeholder="email@example.com"
                  />
                  <InputField
                    isLight={isLight}
                    label="Phone Number"
                    icon={Phone}
                    name="VB_Phone"
                    value={formData.VB_Phone}
                    onChange={handleChange}
                    type="tel"
                    placeholder="e.g. +94 77 123 4567"
                  />
                </div>

                {/* Footer */}
                <div
                  className={`flex gap-2.5 pt-4 border-t ${isLight ? "border-gray-200" : "border-white/5"}`}
                >
                  <button
                    type="button"
                    onClick={onClose}
                    className={`flex-1 py-3 rounded-2xl text-[10px] font-bold uppercase tracking-widest transition-all border ${isLight ? "border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-[#1A1A1A]" : "border-white/10 text-gray-400 hover:bg-white/5 hover:text-white"}`}
                  >
                    Discard Changes
                  </button>
                  <button
                    type="submit"
                    className="flex-[2] py-3 bg-primary hover:bg-primary-hover rounded-2xl text-white text-[10px] font-bold uppercase tracking-widest flex items-center justify-center gap-2.5 hover:scale-[1.02] transition-all shadow-lg shadow-primary/20"
                  >
                    <Save size={15} />
                    Update Details
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default EditBlacklistModal;
